// src/api/useApi.js
import { useContext, useMemo } from 'react';

import makeAuthenticatedRequest, {
  convertUTCToLocal,
  convertLocalToUTC,
  convertLocalDateToUTC,
  formatLocalDate,
  processOutgoingDateFields,
} from '../api';
import { UserContext } from '../context/UserContext';

// Re-export date utilities for backward compatibility
// Components that import from useApi.js will continue to work
export {
  convertUTCToLocal,
  convertLocalToUTC,
  convertLocalDateToUTC,
  formatLocalDate,
};

// In-flight request coalescing for the email-stats dashboard endpoint.
// The home fires this twice on load (UserContext does two setUser() during
// init — JWT then API refresh — which re-mounts the dashboard subtree and
// re-runs the fetch effect with userProjects already populated). The endpoint
// is heavy (~55s cold for large clients), so two identical concurrent requests
// double the DB load. Keyed by the full request path, identical in-flight
// calls share one promise; the entry clears as soon as it settles, so later
// loads still hit the network fresh (no stale cache).
const inFlightEmailStats = new Map();

/**
 * Process email stats data for deduplication.
 *
 * Note: Date field processing (_utc, _display) is now handled automatically
 * by the API layer for all responses. This function only handles
 * email-stats-specific processing like deduplication.
 */
const processEmailStatsData = (data) => {
  if (!data) return data;

  const processedData = { ...data };

  // Process logs array - deduplicate entries
  if (processedData.logs && Array.isArray(processedData.logs)) {
    // Debug: Check for duplicates in raw data from backend
    const duplicatesFromBackend = processedData.logs.filter(
      (log, index, self) =>
        index !==
        self.findIndex(
          (l) =>
            l.recipient === log.recipient &&
            l.sent_at === log.sent_at &&
            l.status === log.status,
        ),
    );

    if (duplicatesFromBackend.length > 0) {
      console.warn(
        `Found ${duplicatesFromBackend.length} duplicate logs from backend:`,
        duplicatesFromBackend,
      );
    }

    // Deduplicate logs based on original values
    const uniqueLogs = processedData.logs.filter(
      (log, index, self) =>
        index ===
        self.findIndex(
          (l) =>
            l.recipient === log.recipient &&
            l.sent_at === log.sent_at &&
            l.status === log.status,
        ),
    );

    if (processedData.logs.length !== uniqueLogs.length) {
      console.log(
        `Deduplication: ${processedData.logs.length} logs → ${uniqueLogs.length} unique logs`,
      );
    }

    processedData.logs = uniqueLogs;
  }

  // Time series data is already in the correct format (YYYY-MM-DD or YYYY-MM-DD HH:MM)
  // No conversion needed for daily_data, hourly_data, minute_data, monthly_data

  return processedData;
};

function buildProductFormData(productData, imageFile) {
  const formData = new FormData();
  Object.entries(productData).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (value instanceof File || value instanceof Blob) {
      formData.append(key, value);
    } else if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  });
  formData.append('raw_image', imageFile);
  return formData;
}

export function useApi() {
  const { refreshToken } = useContext(UserContext);

  return useMemo(
    () => ({
      // --- PROJECT METHODS ---
      getProject: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      getProjects: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      createProject: async (clientId, projectData) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects`,
          refreshToken,
          { method: 'post', data: projectData },
        );
        return res.data;
      },

      getUserProjects: async (clientId, userId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/users/${userId}/projects`,
          refreshToken,
          { method: 'get' },
        );
        // The Flask route returns { projects: [ … ] }
        // Normalize to always return an array:
        return Array.isArray(res.data) ? res.data : res.data.projects || [];
      },

      updateProject: async (clientId, projectId, data) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}`,
          refreshToken,
          { method: 'put', data },
        );
        return res.data;
      },

      deleteProject: async (clientId, projectId) => {
        return await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}`,
          refreshToken,
          { method: 'delete' },
        );
      },

      // USERS — CLIENT LEVEL
      getUsers: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/users`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : res.data.users || [];
      },

      getUser: async (clientId, userId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/users/${userId}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // USERS ASSIGNED TO A PROJECT
      getProjectUsers: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `projects/${projectId}/users`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : res.data.users || [];
      },

      setProjectUsers: async (clientId, projectId, userIds) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/assign-users`,
          refreshToken,
          { method: 'post', data: { user_ids: userIds } },
        );
        return res.data;
      },

      // PROJECT CONTACT ASSIGNMENT

      // Bulk‐assign several contacts to one project:
      assignContactsToProject: async (clientId, projectId, contactIds) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/assign-contacts`,
          refreshToken,
          {
            method: 'post',
            data: { contact_ids: contactIds },
            headers: { 'Content-Type': 'application/json' },
          },
        );
        return res.data; // e.g. { assigned: [1,2], skipped: [3], errors: [...] }
      },

      // If you also want a “single‐contact → many projects” helper:
      assignContactProjects: async (clientId, contactId, projectIds) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/contacts/${contactId}/assign`,
          refreshToken,
          {
            method: 'put',
            data: { project_ids: projectIds },
            headers: { 'Content-Type': 'application/json' },
          },
        );
        return res.data; // whatever that endpoint returns
      },

      unassignContactFromProject: async (clientId, projectId, contactId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/unassign-contact/${contactId}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      getPlans: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/plans`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      getContact: async (clientId, contactId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/contacts/${contactId}`,
          refreshToken,
        );
        return res.data;
      },

      // Get all contacts for a client with optional pagination and filters
      getContacts: async (clientId, filters = {}) => {
        const params = new URLSearchParams(filters);
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/contacts?${params}`,
          refreshToken,
        );
        // Return the full response object with contacts and pagination
        return res.data;
      },

      // Create a contact
      // Note: processOutgoingDateFields converts local datetime strings in custom_data to UTC
      createContact: async (clientId, data) =>
        (
          await makeAuthenticatedRequest(
            `clients/${clientId}/contacts`,
            refreshToken,
            { method: 'post', data: processOutgoingDateFields(data) },
          )
        ).data,

      // Update a contact
      // Note: processOutgoingDateFields converts local datetime strings in custom_data to UTC
      updateContact: async (clientId, contactId, data) =>
        (
          await makeAuthenticatedRequest(
            `clients/${clientId}/contacts/${contactId}`,
            refreshToken,
            { method: 'put', data: processOutgoingDateFields(data) },
          )
        ).data,

      // Delete a contact
      deleteContact: async (clientId, contactId) =>
        (
          await makeAuthenticatedRequest(
            `clients/${clientId}/contacts/${contactId}`,
            refreshToken,
            { method: 'delete' },
          )
        ).data,

      // Upload contacts (bulk)
      uploadContacts: async (clientId, formData) =>
        (
          await makeAuthenticatedRequest(
            `clients/${clientId}/contacts/bulk-upload`,
            refreshToken,
            {
              method: 'post',
              data: formData,
              headers: { 'Content-Type': 'multipart/form-data' },
            },
          )
        ).data,

      // Assignment templates
      getAssignmentTemplate: async (clientId, projectId) =>
        (
          await makeAuthenticatedRequest(
            `clients/${clientId}/projects/${projectId}/assignment-template`,
            refreshToken,
          )
        ).data,

      updateAssignmentTemplate: async (clientId, projectId, custom_keys) =>
        (
          await makeAuthenticatedRequest(
            `clients/${clientId}/projects/${projectId}/assignment-template`,
            refreshToken,
            { method: 'put', data: { custom_keys } },
          )
        ).data,

      syncAssignmentFields: async (
        clientId,
        projectId,
        custom_keys,
        default_values,
      ) =>
        (
          await makeAuthenticatedRequest(
            `clients/${clientId}/projects/${projectId}/contacts/custom-fields`,
            refreshToken,
            {
              method: 'patch',
              data: { custom_keys: Object.keys(custom_keys), default_values },
            },
          )
        ).data,

      getProjectContacts: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/contacts`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : [];
      },

      updateContactAssignmentCustom: async (
        clientId,
        contactId,
        projectId,
        custom_data,
      ) => {
        // PATCH /clients/:clientId/contacts/:contactId/projects/:projectId/custom-fields
        // Note: processOutgoingDateFields converts local datetime strings to UTC
        return await makeAuthenticatedRequest(
          `clients/${clientId}/contacts/${contactId}/projects/${projectId}/custom-fields`,
          refreshToken,
          { method: 'patch', data: { custom_data: processOutgoingDateFields(custom_data) } },
        );
      },

      getProjectEmailTemplates: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-templates`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : res.data.templates || [];
      },

      saveProjectEmailTemplate: async (
        clientId,
        projectId,
        actionType,
        data,
      ) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-templates/${actionType}`,
          refreshToken,
          {
            method: 'put',
            data: data,
            headers: { 'Content-Type': 'application/json' },
          },
        );
        return res.data;
      },

      deleteProjectEmailTemplate: async (clientId, projectId, actionType) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-templates/${actionType}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // ============================================
      // AI HTML BUILDER API METHODS
      // ============================================

      /**
       * Get available email template types for AI generation
       */
      getAITemplateTypes: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-templates/ai/template-types`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      /**
       * Get brand identity preview for client (colors, fonts, logo)
       */
      getAIBrandPreview: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-templates/ai/brand-preview`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      /**
       * Generate an email template using AI
       * @param {Object} options - { template_type, purpose, custom_instructions, sequence_position }
       */
      generateAIEmailTemplate: async (clientId, projectId, options) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-templates/ai/generate`,
          refreshToken,
          {
            method: 'post',
            data: options,
            headers: { 'Content-Type': 'application/json' },
          },
        );
        return res.data;
      },

      /**
       * Get subject line suggestions from AI
       * @param {Object} options - { template_type, purpose, count }
       */
      suggestAISubjectLines: async (clientId, projectId, options) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-templates/ai/suggest-subjects`,
          refreshToken,
          {
            method: 'post',
            data: options,
            headers: { 'Content-Type': 'application/json' },
          },
        );
        return res.data;
      },

      /**
       * Improve email copy using AI
       * @param {Object} options - { current_html, improvement_type }
       */
      improveAIEmailCopy: async (clientId, projectId, options) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-templates/ai/improve`,
          refreshToken,
          {
            method: 'post',
            data: options,
            headers: { 'Content-Type': 'application/json' },
          },
        );
        return res.data;
      },

      /**
       * Regenerate a specific section of an email
       * @param {Object} options - { section, current_html, instructions }
       */
      regenerateAISection: async (clientId, projectId, options) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-templates/ai/regenerate-section`,
          refreshToken,
          {
            method: 'post',
            data: options,
            headers: { 'Content-Type': 'application/json' },
          },
        );
        return res.data;
      },

      /**
       * Re-render HTML from edited content data using Jinja2 templates
       * @param {Object} options - { template_type, content_data, custom_colors }
       */
      renderAITemplateFromContent: async (clientId, projectId, options) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-templates/ai/render-from-content`,
          refreshToken,
          {
            method: 'post',
            data: options,
            headers: { 'Content-Type': 'application/json' },
          },
        );
        return res.data;
      },

      /**
       * Import an HTML email template from an external platform (Canva, etc.)
       * @param {File} file - HTML file to import
       * @param {string} [subject] - Optional subject override
       */
      importHtmlTemplate: async (clientId, projectId, file, subject) => {
        const formData = new FormData();
        formData.append('file', file);
        if (subject) formData.append('subject', subject);
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-templates/import-html`,
          refreshToken,
          {
            method: 'post',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        );
        return res.data;
      },

      getProjectContactFields: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/contacts/fields`,
          refreshToken,
          { method: 'get' },
        );
        // Backend returns { fields: [...] }
        return Array.isArray(res.data) ? res.data : res.data?.fields || [];
      },

      updateAction: async (actionId, data) => {
        const res = await makeAuthenticatedRequest(
          `actions/${actionId}`,
          refreshToken,
          { method: 'put', data },
        );
        return res.data; // returns the updated action
      },
      // —— FILE TEMPLATES CRUD ——
      listProjectFiles: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/files`,
          refreshToken,
          { method: 'get' },
        );
        // the backend returns an array of file dicts
        return Array.isArray(res.data) ? res.data : res.data.files || [];
      },

      uploadProjectFiles: async (clientId, projectId, files) => {
        const form = new FormData();
        files.forEach((f) => form.append('files', f));
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/files`,
          refreshToken,
          {
            method: 'post',
            data: form,
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        );
        // returns { message, files: [...] }
        return res.data.files || [];
      },

      deleteProjectFile: async (clientId, projectId, filename) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/files/${encodeURIComponent(
            filename,
          )}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // ── SUPPRESSION LISTS ────────────────────────────────────────────
      checkSuppressionStatus: async (clientId, emails) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/suppression/check`,
          refreshToken,
          {
            method: 'post',
            data: { emails: Array.isArray(emails) ? emails : [emails] },
          },
        );
        return res.data; // returns { email: boolean } mapping
      },

      // ── NOTIFICATIONS ────────────────────────────────────────────────
      listNotifications: async (status) => {
        let url = 'notifications';
        if (status) {
          url += `?status=${status}`;
        }
        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return Array.isArray(res.data) ? res.data : [];
      },

      getUserNotifications: async (params = {}) => {
        const { page = 1, limit = 10, status, type, search, module_id, sort = 'desc' } = params;
        let url = `notifications/user?page=${page}&limit=${limit}&sort=${sort}`;
        if (status) url += `&status=${status}`;
        if (type) url += `&type=${type}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (module_id) url += `&module_id=${module_id}`;
        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return res.data;
      },

      getNotificationModules: async () => {
        const res = await makeAuthenticatedRequest('notification-modules', refreshToken, {
          method: 'get',
        });
        return res.data;
      },

      markNotificationRead: async (notificationId) => {
        const res = await makeAuthenticatedRequest(
          `notifications/${notificationId}`,
          refreshToken,
          { method: 'patch', data: { status: 'read' } },
        );
        return res.data;
      },

      markAllNotificationsRead: async () => {
        const res = await makeAuthenticatedRequest(
          'notifications/mark-all-read',
          refreshToken,
          { method: 'patch' },
        );
        return res.data;
      },

      getProjectFileDownloadUrl: (clientId, projectId, filename) =>
        // you can use this URL directly in an <a href="…" download>
        `api/clients/${clientId}/projects/${projectId}/files/${encodeURIComponent(
          filename,
        )}`,

      getProjectEmailStats: async (clientId, projectId, params = '') => {
        // If params is a URLSearchParams object or string, we need to process date parameters
        let processedParams = params;
        if (params) {
          const urlParams = new URLSearchParams(params);
          const startDate = urlParams.get('start_date');
          const endDate = urlParams.get('end_date');

          // Convert local dates to UTC for backend filtering
          if (startDate) {
            urlParams.set(
              'start_date',
              convertLocalDateToUTC(startDate, false),
            );
          }
          if (endDate) {
            urlParams.set('end_date', convertLocalDateToUTC(endDate, true));
          }

          processedParams = urlParams.toString();
        }

        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-stats${
            processedParams ? `?${processedParams}` : ''
          }`,
          refreshToken,
          { method: 'get' },
        );
        return processEmailStatsData(res.data);
      },

      // Lightweight project summary (counts only, no full data load)
      getLeadsSummary: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/leads/summary`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // Send test email for a project template
      sendProjectTestEmail: async (
        clientId,
        projectId,
        email,
        templateType = 'LEAD_1',
      ) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/test-email`,
          refreshToken,
          {
            method: 'post',
            data: { email, template_type: templateType },
          },
        );
        return res.data;
      },

      getClientEmailStats: async (clientId, params = '') => {
        // If params is a URLSearchParams object or string, we need to process date parameters
        let processedParams = params;
        if (params) {
          const urlParams = new URLSearchParams(params);
          const startDate = urlParams.get('start_date');
          const endDate = urlParams.get('end_date');

          // Convert local dates to UTC for backend filtering
          if (startDate) {
            urlParams.set(
              'start_date',
              convertLocalDateToUTC(startDate, false),
            );
          }
          if (endDate) {
            urlParams.set('end_date', convertLocalDateToUTC(endDate, true));
          }

          processedParams = urlParams.toString();
        }

        const path = `clients/${clientId}/email-stats${
          processedParams ? `?${processedParams}` : ''
        }`;

        // Coalesce identical in-flight requests so the home's duplicate fetch
        // (see inFlightEmailStats above) only hits the backend once.
        if (inFlightEmailStats.has(path)) {
          return inFlightEmailStats.get(path);
        }

        const promise = makeAuthenticatedRequest(path, refreshToken, {
          method: 'get',
        }).then((res) => processEmailStatsData(res.data));
        inFlightEmailStats.set(path, promise);
        promise.finally(() => inFlightEmailStats.delete(path));
        return promise;
      },

      exportProjectEmailStats: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/email-stats/export`,
          refreshToken,
          {
            method: 'get',
            responseType: 'blob',
          },
        );
        return res.data;
      },

      // ── EMAIL CONFIG ─────────────────────────────────────────────────────
      getEmailConfig: async () => {
        const res = await makeAuthenticatedRequest(
          'email-config',
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // ── EMAIL CONFIG OAuth Status ────────────────────────────────────────
      getOAuthStatus: async () => {
        const res = await makeAuthenticatedRequest(
          'email-config/oauth-status',
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // ── SENDGRID CONFIGURATION ────────────────────────────────────────────
      getSendGridStatus: async () => {
        const res = await makeAuthenticatedRequest(
          'sendgrid/status',
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      setupSendGridSingleSender: async (email, fromName) => {
        const res = await makeAuthenticatedRequest(
          'sendgrid/setup-single-sender',
          refreshToken,
          {
            method: 'post',
            data: { email, from_name: fromName },
          },
        );
        return res.data;
      },

      verifySendGridSingleSender: async () => {
        const res = await makeAuthenticatedRequest(
          'sendgrid/verify-single-sender',
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      verifySendGridToken: async (token) => {
        const res = await makeAuthenticatedRequest(
          'sendgrid/verify-token',
          refreshToken,
          {
            method: 'post',
            data: { token },
          },
        );
        return res.data;
      },

      // ── SEGMENTATIONS ────────────────────────────────────────────────

      // Get available fields for segmentation
      getAvailableSegmentationFields: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/available-fields`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // List all segmentations
      listSegmentations: async (clientId, params = {}) => {
        const { type, include_details } = params;
        let url = `clients/${clientId}/segmentations`;
        const queryParams = [];
        if (type) queryParams.push(`type=${type}`);
        if (include_details)
          queryParams.push(`include_details=${include_details}`);
        if (queryParams.length > 0) url += `?${queryParams.join('&')}`;

        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return Array.isArray(res.data) ? res.data : [];
      },

      // Get specific segmentation
      getSegmentation: async (clientId, segmentationId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // Create new segmentation
      createSegmentation: async (clientId, data) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations`,
          refreshToken,
          { method: 'post', data },
        );
        return res.data;
      },

      // Update segmentation
      updateSegmentation: async (clientId, segmentationId, data) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}`,
          refreshToken,
          { method: 'put', data },
        );
        return res.data;
      },

      // Delete segmentation
      deleteSegmentation: async (clientId, segmentationId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // Execute segmentation (run rules and populate groups with contacts)
      executeSegmentation: async (clientId, segmentationId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/execute`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      // ── AI RECOMMENDATIONS ──────────────────────────────────────────

      // Generate AI-powered segmentation recommendations
      generateAIRecommendations: async (clientId, maxRecommendations = 5) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/ai-recommendations`,
          refreshToken,
          { method: 'post', data: { max_recommendations: maxRecommendations } },
        );
        return res.data;
      },

      // List AI-recommended segmentations
      listAIRecommendations: async (clientId, activated = 'all') => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/ai-recommendations?activated=${activated}`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : [];
      },

      // Activate an AI-recommended segmentation
      activateAIRecommendation: async (clientId, segmentationId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/activate`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      // Deactivate an AI-recommended segmentation
      deactivateAIRecommendation: async (clientId, segmentationId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/deactivate`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      // ── GROUPS (Rule-based segments) ────────────────────────────────

      // List groups in a segmentation
      listGroups: async (clientId, segmentationId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/groups`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : [];
      },

      // Get specific group
      // includeContacts: 'full' (default), 'ids' (lightweight), or 'none'
      getGroup: async (clientId, segmentationId, groupId, includeContacts = 'full') => {
        const params = includeContacts !== 'full' ? `?include_contacts=${includeContacts}` : '';
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/groups/${groupId}${params}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // Batch get group memberships for multiple contacts (much more efficient!)
      // Instead of fetching 100k+ contacts per group, fetch memberships for the visible contacts
      getBatchContactGroups: async (clientId, contactIds) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/contacts/batch-groups`,
          refreshToken,
          { method: 'post', data: { contact_ids: contactIds } },
        );
        return res.data?.memberships || {};
      },

      // Create new group
      createGroup: async (clientId, segmentationId, data) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/groups`,
          refreshToken,
          { method: 'post', data },
        );
        return res.data;
      },

      // Update group
      updateGroup: async (clientId, segmentationId, groupId, data) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/groups/${groupId}`,
          refreshToken,
          { method: 'put', data },
        );
        return res.data;
      },

      // Delete group
      deleteGroup: async (clientId, segmentationId, groupId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/groups/${groupId}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // Assign contacts to group
      assignContactsToGroup: async (
        clientId,
        segmentationId,
        groupId,
        contactIds,
      ) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/groups/${groupId}/contacts`,
          refreshToken,
          { method: 'post', data: { contact_ids: contactIds } },
        );
        return res.data;
      },

      // Remove contact from group
      removeContactFromGroup: async (
        clientId,
        segmentationId,
        groupId,
        contactId,
      ) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/groups/${groupId}/contacts/${contactId}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // Export group contacts to CSV
      exportGroupContactsCSV: async (clientId, segmentationId, groupId) => {
        const response = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/groups/${groupId}/export-csv`,
          refreshToken,
          {
            method: 'get',
            responseType: 'blob',
          },
        );
        return response.data;
      },

      // ── CLUSTERS (ML-based segments) ────────────────────────────────

      // List clusters in a segmentation
      listClusters: async (clientId, segmentationId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/clusters`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : [];
      },

      // Get specific cluster
      getCluster: async (clientId, segmentationId, clusterId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/clusters/${clusterId}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // Create new cluster
      createCluster: async (clientId, segmentationId, data) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/clusters`,
          refreshToken,
          { method: 'post', data },
        );
        return res.data;
      },

      // Update cluster
      updateCluster: async (clientId, segmentationId, clusterId, data) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/clusters/${clusterId}`,
          refreshToken,
          { method: 'put', data },
        );
        return res.data;
      },

      // Delete cluster
      deleteCluster: async (clientId, segmentationId, clusterId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/clusters/${clusterId}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // Assign contacts to cluster
      assignContactsToCluster: async (
        clientId,
        segmentationId,
        clusterId,
        contactIds,
      ) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/clusters/${clusterId}/contacts`,
          refreshToken,
          { method: 'post', data: { contact_ids: contactIds } },
        );
        return res.data;
      },

      // Remove contact from cluster
      removeContactFromCluster: async (
        clientId,
        segmentationId,
        clusterId,
        contactId,
      ) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/clusters/${clusterId}/contacts/${contactId}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // Export cluster contacts to CSV
      exportClusterContactsCSV: async (clientId, segmentationId, clusterId) => {
        const response = await makeAuthenticatedRequest(
          `clients/${clientId}/segmentations/${segmentationId}/clusters/${clusterId}/export-csv`,
          refreshToken,
          {
            method: 'get',
            responseType: 'blob',
          },
        );
        return response.data;
      },

      // ── CONTACT SEGMENT LOOKUPS ─────────────────────────────────────

      // Get all groups a contact belongs to
      getContactGroups: async (clientId, contactId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/contacts/${contactId}/groups`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : [];
      },

      // Get all clusters a contact belongs to
      getContactClusters: async (clientId, contactId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/contacts/${contactId}/clusters`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : [];
      },

      // ── GROUP/CLUSTER → PROJECT ASSIGNMENT ──────────────────────────────

      // Assign group to project
      assignGroupToProject: async (
        clientId,
        projectId,
        groupId,
        autoSync = true,
      ) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/assign-group`,
          refreshToken,
          {
            method: 'post',
            data: { group_id: groupId, auto_sync: autoSync },
          },
        );
        return res.data;
      },

      // Assign cluster to project
      assignClusterToProject: async (
        clientId,
        projectId,
        clusterId,
        autoSync = true,
      ) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/assign-cluster`,
          refreshToken,
          {
            method: 'post',
            data: { cluster_id: clusterId, auto_sync: autoSync },
          },
        );
        return res.data;
      },

      // List groups assigned to a project
      listAssignedGroups: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/assigned-groups`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : [];
      },

      // List clusters assigned to a project
      listAssignedClusters: async (clientId, projectId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/assigned-clusters`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : [];
      },

      // Unassign group from project
      unassignGroupFromProject: async (
        clientId,
        projectId,
        groupId,
        action = 'keep',
      ) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/unassign-group/${groupId}?action=${action}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // Unassign cluster from project
      unassignClusterFromProject: async (
        clientId,
        projectId,
        clusterId,
        action = 'keep',
      ) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/unassign-cluster/${clusterId}?action=${action}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // Preview group assignment impact
      previewGroupAssignment: async (clientId, projectId, groupId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/preview-group-assignment/${groupId}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // Preview cluster assignment impact
      previewClusterAssignment: async (clientId, projectId, clusterId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/preview-cluster-assignment/${clusterId}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // Manually sync group with project
      syncGroupWithProject: async (clientId, projectId, groupId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/sync-group/${groupId}`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      // Manually sync cluster with project
      syncClusterWithProject: async (clientId, projectId, clusterId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/projects/${projectId}/sync-cluster/${clusterId}`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      // Sync group with all assigned projects
      syncGroupAllProjects: async (clientId, groupId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/groups/${groupId}/sync-all-projects`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      // Sync cluster with all assigned projects
      syncClusterAllProjects: async (clientId, clusterId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/clusters/${clusterId}/sync-all-projects`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      getDailyLimits: async () => {
        const res = await makeAuthenticatedRequest(
          'email-config/daily-limits',
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // ── CLIENTS ────────────────────────────────────────────

      // Get all clients (superadmin only)
      // simple=true returns only id and name (for dropdowns, much faster)
      getClients: async (simple = false) => {
        const url = simple ? 'clients?simple=true' : 'clients';
        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return res.data; // Returns array of clients
      },

      // ── MODULES ────────────────────────────────────────────

      // Get all modules (admin/superadmin)
      getModules: async () => {
        const res = await makeAuthenticatedRequest('modules', refreshToken, {
          method: 'get',
        });
        return res.data; // Returns array of modules
      },

      // ── PLANS & SUBSCRIPTIONS ────────────────────────────────────────────

      // Get all plans (superadmin only - includes public and non-public)
      getAllPlans: async () => {
        const res = await makeAuthenticatedRequest('plans', refreshToken, {
          method: 'get',
        });
        return res.data; // { success: true, plans: [...] }
      },

      // Get public plans (for client-facing plan selection)
      getPublicPlans: async () => {
        const res = await makeAuthenticatedRequest(
          'plans/public',
          refreshToken,
          {
            method: 'get',
          },
        );
        return res.data; // { success: true, plans: [...] }
      },

      // Get specific plan details
      getPlanDetails: async (planId) => {
        const res = await makeAuthenticatedRequest(
          `plans/${planId}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data; // { success: true, plan: {...} }
      },

      // Create new plan (superadmin only)
      createPlan: async (planData) => {
        const res = await makeAuthenticatedRequest('plans', refreshToken, {
          method: 'post',
          data: planData,
        });
        return res.data;
      },

      // Update plan (superadmin only)
      updatePlan: async (planId, planData) => {
        const res = await makeAuthenticatedRequest(
          `plans/${planId}`,
          refreshToken,
          { method: 'put', data: planData },
        );
        return res.data;
      },

      // Get current user's subscription
      getCurrentSubscription: async () => {
        const res = await makeAuthenticatedRequest(
          'subscriptions/current',
          refreshToken,
          { method: 'get' },
        );
        return res.data; // { success: true, subscription: {...} }
      },

      // Get subscription for specific client (admin/superadmin)
      getClientSubscription: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `subscriptions/client/${clientId}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data; // { success: true, subscription: {...}, client: {...} }
      },

      // Get all subscriptions (superadmin only)
      getAllSubscriptions: async (status = null, page = 1, perPage = 50) => {
        let url = `subscriptions/all?page=${page}&per_page=${perPage}`;
        if (status) url += `&status=${status}`;

        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return res.data; // { success: true, subscriptions: [...], total, page, per_page, total_pages }
      },

      // Client-centric subscription overview (superadmin only)
      // One row per client (head of chain) + KPI counts. Used by /admin/subscriptions.
      getSubscriptionsOverview: async (filters = {}) => {
        const params = new URLSearchParams();
        const {
          search,
          status,
          expiringInDays,
          hasPendingChange,
          missingPeriodBill,
          includeNoActive,
          includeBillingDisabled,
          page = 1,
          perPage = 50,
        } = filters;

        if (search) params.set('search', search);
        if (status) params.set('status', status);
        if (expiringInDays != null) params.set('expiring_in_days', expiringInDays);
        if (hasPendingChange != null) params.set('has_pending_change', hasPendingChange);
        if (missingPeriodBill) params.set('missing_period_bill', 'true');
        if (includeNoActive) params.set('include_no_active', 'true');
        if (includeBillingDisabled) params.set('include_billing_disabled', 'true');
        params.set('page', page);
        params.set('per_page', perPage);

        const res = await makeAuthenticatedRequest(
          `subscriptions/overview?${params.toString()}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data; // { success, rows: [...], total, page, per_page, total_pages, kpis: {...} }
      },

      // Set (or replace) the recurring discount for a subscription — the
      // single source of truth for the effective price (bills + MP
      // autopay preapproval sync). Superadmin only. See
      // backend/src/services/billing_pricing.normalize_recurring_discount
      // for the canonical payload shape.
      setRecurringDiscount: async (subId, payload) => {
        const res = await makeAuthenticatedRequest(
          `billing/admin/subscriptions/${subId}/recurring-discount`,
          refreshToken,
          { method: 'put', data: payload },
        );
        return res.data; // { success, recurring_discount, effective_amount, warning? }
      },

      // Remove the recurring discount for a subscription (idempotent, superadmin only)
      removeRecurringDiscount: async (subId) => {
        const res = await makeAuthenticatedRequest(
          `billing/admin/subscriptions/${subId}/recurring-discount`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data; // { success }
      },

      // Límites por módulo de una marca (superadmin; paywall §8). El GET trae el
      // registro canónico completo; PUT y DELETE devuelven ese mismo cuerpo más
      // `changed`. Los errores 400/404/409 propagan con `{ error, message }`.
      getClientModuleLimits: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `admin/clients/${clientId}/module-limits`,
          refreshToken,
          { method: 'get' },
        );
        return res.data; // { client_id, subscription, modules: [...] }
      },

      // `overrides`: { [módulo]: { limit_value, cap_behavior?, note? } | null } (merge)
      setClientModuleLimits: async (clientId, overrides) => {
        const res = await makeAuthenticatedRequest(
          `admin/clients/${clientId}/module-limits`,
          refreshToken,
          { method: 'put', data: { overrides } },
        );
        return res.data; // { ...GET, changed: [...] }
      },

      // Quita el override de un módulo (idempotente)
      removeClientModuleLimit: async (clientId, moduleName) => {
        const res = await makeAuthenticatedRequest(
          `admin/clients/${clientId}/module-limits/${moduleName}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data; // { ...GET, changed: [...] }
      },

      // Generate the missing first-period bill for a subscription (superadmin)
      generateMissingBill: async (subscriptionId) => {
        const res = await makeAuthenticatedRequest(
          `subscriptions/${subscriptionId}/generate-missing-bill`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      // Cancel a pending plan change (scheduled downgrade) — superadmin only
      cancelPendingChange: async (subscriptionId) => {
        const res = await makeAuthenticatedRequest(
          `subscriptions/${subscriptionId}/pending-change`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // Create subscription for a client (admin/superadmin)
      createSubscription: async (clientId, planId, options = {}) => {
        const res = await makeAuthenticatedRequest(
          'subscriptions',
          refreshToken,
          {
            method: 'post',
            data: {
              client_id: clientId,
              plan_id: planId,
              ...options, // proration_enabled, payment_method_ref, external_id, metadata
            },
          },
        );
        return res.data;
      },

      // Cancel subscription
      cancelSubscription: async (subscriptionId, immediate = false) => {
        const res = await makeAuthenticatedRequest(
          `subscriptions/${subscriptionId}/cancel`,
          refreshToken,
          {
            method: 'post',
            data: { immediate },
          },
        );
        return res.data;
      },

      // Upgrade subscription (immediate, higher-priced plan)
      upgradeSubscription: async (
        subscriptionId,
        newPlanId,
        resetUsage = false,
      ) => {
        const res = await makeAuthenticatedRequest(
          `subscriptions/${subscriptionId}/upgrade`,
          refreshToken,
          {
            method: 'post',
            data: {
              plan_id: newPlanId,
              reset_usage: resetUsage,
            },
          },
        );
        return res.data;
      },

      // Downgrade subscription (scheduled at end of period, lower-priced plan)
      downgradeSubscription: async (subscriptionId, newPlanId) => {
        const res = await makeAuthenticatedRequest(
          `subscriptions/${subscriptionId}/downgrade`,
          refreshToken,
          {
            method: 'post',
            data: { plan_id: newPlanId },
          },
        );
        return res.data;
      },

      // Get usage summary for current user's client
      getUsageSummary: async () => {
        const res = await makeAuthenticatedRequest(
          'usage/summary',
          refreshToken,
          { method: 'get' },
        );
        return res.data; // { success: true, usage: {...} }
      },

      // Consume quota for a module (admin/supervisor)
      consumeModuleUsage: async (
        moduleId,
        amount = 1,
        projectId = null,
        force = false,
        metadata = null,
      ) => {
        const res = await makeAuthenticatedRequest(
          'usage/consume',
          refreshToken,
          {
            method: 'post',
            data: {
              module_id: moduleId,
              amount,
              project_id: projectId,
              force,
              metadata,
            },
          },
        );
        return res.data;
      },

      // Get overage charges for billing period
      getOverageCharges: async (startDate = null, endDate = null) => {
        let url = 'usage/overage';
        const params = [];
        if (startDate) params.push(`start_date=${startDate}`);
        if (endDate) params.push(`end_date=${endDate}`);
        if (params.length > 0) url += `?${params.join('&')}`;

        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return res.data;
      },

      // Get usage history with pagination
      getUsageHistory: async (page = 1, perPage = 50, moduleId = null) => {
        let url = `usage/history?page=${page}&per_page=${perPage}`;
        if (moduleId) url += `&module_id=${moduleId}`;

        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return res.data; // { success: true, history: [...], total, page, per_page, total_pages }
      },

      // ── E-COMMERCE INTEGRATIONS (Admin/Superadmin) ────────────────────────────────────

      // Get all Justo integrations (superadmin only)
      getAllJustoIntegrations: async () => {
        const res = await makeAuthenticatedRequest(
          'admin/justo/integrations',
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : res.data.integrations || [];
      },

      // Get Justo integrations for the authenticated user's client
      getJustoIntegrations: async () => {
        const res = await makeAuthenticatedRequest(
          'justo/integrations',
          refreshToken,
          { method: 'get' },
        );
        return res.data?.integrations || [];
      },

      // Get all Shopify integrations (superadmin only)
      getAllShopifyIntegrations: async () => {
        const res = await makeAuthenticatedRequest(
          'admin/shopify/integrations',
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : res.data.integrations || [];
      },

      // Get Shopify integrations for the authenticated user's client
      getShopifyIntegrations: async () => {
        const res = await makeAuthenticatedRequest(
          'shopify/integrations',
          refreshToken,
          { method: 'get' },
        );
        return res.data?.integrations || [];
      },

      // Initiate Shopify OAuth install flow (client level) - LEGACY
      // Returns an install_url that should be opened for the shop owner to authorize
      // NOTE: Use getShopifyInstallLink for App Store compliant flow
      initiateShopifyInstallClient: async (clientId, shopDomain) => {
        const res = await makeAuthenticatedRequest(
          'shopify/install',
          refreshToken,
          {
            method: 'post',
            data: {
              client_id: clientId,
              shop_domain: shopDomain,
            },
          },
        );
        return res.data;
      },

      // Get Shopify install link (Shopify-managed OAuth flow)
      // No shop_domain required - merchant selects store in Shopify's UI
      // Compliant with Shopify App Store requirements
      getShopifyInstallLink: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          'shopify/install-link',
          refreshToken,
          {
            method: 'post',
            data: clientId ? { client_id: clientId } : {},
          },
        );
        return res.data;
      },

      // Deactivate Shopify integration (client level)
      deleteShopifyIntegration: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          `shopify/integrations/${integrationId}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // Test Shopify integration connection
      testShopifyIntegration: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          `shopify/integrations/${integrationId}/test`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      // Re-trigger webhook registration for a Shopify integration
      // Returns 202 Accepted with { task_id, message }
      ensureShopifyWebhooks: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          `shopify/integrations/${integrationId}/ensure-webhooks`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      // Get active store-wide Shopify discount codes for a client. Returns
      // { codes: [{code, title, ends_at, type}], needs_reauth, connected }.
      // Used by the email builder to offer auto-apply discount selection.
      getShopifyDiscountCodes: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/shopify/discount-codes`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // Get Shopify profiling configuration
      getShopifyProfilingConfig: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          `shopify/config/integrations/${integrationId}/profiling-config`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // Update Shopify profiling configuration
      updateShopifyProfilingConfig: async (integrationId, config) => {
        const res = await makeAuthenticatedRequest(
          `shopify/config/integrations/${integrationId}/profiling-config`,
          refreshToken,
          { method: 'put', data: config },
        );
        return res.data;
      },

      // Get Shopify automations configuration
      getShopifyAutomations: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          `shopify/config/integrations/${integrationId}/automations`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // Update a Shopify automation configuration
      updateShopifyAutomation: async (integrationId, data) => {
        const res = await makeAuthenticatedRequest(
          `shopify/config/integrations/${integrationId}/automations`,
          refreshToken,
          { method: 'put', data },
        );
        return res.data;
      },

      // Create Justo integration (admin/superadmin)
      createJustoIntegration: async (clientId, apiKeyV3, apiKeyV2) => {
        const data = { client_id: clientId };
        if (apiKeyV3) data.api_key = apiKeyV3;
        if (apiKeyV2) data.api_key_v2 = apiKeyV2;

        const res = await makeAuthenticatedRequest(
          'justo/integrations',
          refreshToken,
          {
            method: 'post',
            data,
          },
        );
        return res.data;
      },

      // Update Justo integration (admin/superadmin)
      adminUpdateJustoIntegration: async (integrationId, updates) => {
        const res = await makeAuthenticatedRequest(
          `admin/justo/integrations/${integrationId}`,
          refreshToken,
          {
            method: 'patch',
            data: updates,
          },
        );
        return res.data;
      },

      // Update Justo integration (admin/superadmin)
      updateJustoIntegration: async (integrationId, updates) => {
        const res = await makeAuthenticatedRequest(
          `justo/integrations/${integrationId}`,
          refreshToken,
          {
            method: 'patch',
            data: updates,
          },
        );
        return res.data;
      },

      // Get Justo profiling configuration
      getJustoProfilingConfig: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          `justo/integrations/${integrationId}/profiling-config`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // Update Justo profiling configuration
      updateJustoProfilingConfig: async (integrationId, config) => {
        const res = await makeAuthenticatedRequest(
          `justo/integrations/${integrationId}/profiling-config`,
          refreshToken,
          { method: 'put', data: config },
        );
        return res.data;
      },

      // Reset Justo profiling configuration to defaults
      resetJustoProfilingConfig: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          `justo/integrations/${integrationId}/profiling-config`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      // Trigger Justo integration sync (admin endpoints for superadmin)
      triggerJustoSync: async (integrationId, fullSync = false) => {
        const endpoint = fullSync
          ? `admin/justo/integrations/${integrationId}/initial-sync`
          : `admin/justo/integrations/${integrationId}/sync`;

        const res = await makeAuthenticatedRequest(endpoint, refreshToken, {
          method: 'post',
        });
        return res.data;
      },

      // Trigger Justo product sync (admin endpoint for superadmin)
      // Syncs products from menu-costs and updates images from V2 menu
      triggerJustoProductSync: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          `admin/justo/integrations/${integrationId}/sync-products`,
          refreshToken,
          {
            method: 'post',
          },
        );
        return res.data;
      },

      // Trigger Shopify integration sync (admin endpoint for superadmin)
      // fullSync: if true, syncs all historical data; if false (default), incremental sync
      triggerShopifySync: async (integrationId, fullSync = false) => {
        const res = await makeAuthenticatedRequest(
          `admin/shopify/integrations/${integrationId}/sync?full_sync=${fullSync}`,
          refreshToken,
          {
            method: 'post',
          },
        );
        return res.data;
      },

      // Backfill Shopify profiles (superadmin only) - recalculates behavioral tags + lifecycle stage
      backfillShopifyProfiles: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          'admin/tasks/shopify/backfill-profiles',
          refreshToken,
          {
            method: 'post',
            data: { integration_id: integrationId },
          },
        );
        return res.data;
      },

      // Initiate Shopify OAuth install flow (superadmin only)
      // Returns an install_url that should be opened for the shop owner to authorize
      initiateShopifyInstall: async (clientId, shopDomain) => {
        const res = await makeAuthenticatedRequest(
          'admin/shopify/integrations/install',
          refreshToken,
          {
            method: 'post',
            data: {
              client_id: clientId,
              shop_domain: shopDomain,
            },
          },
        );
        return res.data;
      },

      // Create Shopify integration directly with access token (superadmin only)
      // Use initiateShopifyInstall for the proper OAuth flow
      createShopifyIntegration: async (clientId, shopDomain, accessToken) => {
        const res = await makeAuthenticatedRequest(
          'admin/shopify/integrations',
          refreshToken,
          {
            method: 'post',
            data: {
              client_id: clientId,
              shop_domain: shopDomain,
              access_token: accessToken,
            },
          },
        );
        return res.data;
      },

      // Update Shopify integration (superadmin only)
      adminUpdateShopifyIntegration: async (integrationId, updates) => {
        const res = await makeAuthenticatedRequest(
          `admin/shopify/integrations/${integrationId}`,
          refreshToken,
          {
            method: 'patch',
            data: updates,
          },
        );
        return res.data;
      },

      // Get Shopify billing report as JSON preview (superadmin only)
      getShopifyBillingReport: async (startDate, endDate, usdToClpRate, format = 'json') => {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          usd_to_clp_rate: usdToClpRate,
          format,
        });
        const res = await makeAuthenticatedRequest(
          `admin/shopify/billing-report?${params}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // Download Shopify billing report as CSV blob (superadmin only)
      downloadShopifyBillingReportCSV: async (startDate, endDate, usdToClpRate) => {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          usd_to_clp_rate: usdToClpRate,
          format: 'csv',
        });
        const res = await makeAuthenticatedRequest(
          `admin/shopify/billing-report?${params}`,
          refreshToken,
          {
            method: 'get',
            responseType: 'blob',
          },
        );
        return res.data;
      },

      // ========== ANALYTICS & PRODUCTS ==========

      // Get sales timeline with campaign markers
      getSalesTimeline: async (
        projectId,
        startDate,
        endDate,
        includeCampaigns = true,
        additionalParams = {},
      ) => {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          include_campaigns: includeCampaigns,
        });
        // Add additional params (e.g., store_id)
        Object.keys(additionalParams).forEach((key) => {
          if (
            additionalParams[key] !== undefined &&
            additionalParams[key] !== null
          ) {
            params.append(key, additionalParams[key]);
          }
        });
        const res = await makeAuthenticatedRequest(
          `projects/${projectId}/sales-timeline?${params}`,
          refreshToken,
        );
        return res.data;
      },

      // Get client sales timeline (all projects)
      getClientSalesTimeline: async (
        clientId,
        startDate,
        endDate,
        includeCampaigns = true,
        additionalParams = {},
      ) => {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          include_campaigns: includeCampaigns,
        });
        // Add additional params (e.g., store_id)
        Object.keys(additionalParams).forEach((key) => {
          if (
            additionalParams[key] !== undefined &&
            additionalParams[key] !== null
          ) {
            params.append(key, additionalParams[key]);
          }
        });
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/sales-timeline?${params}`,
          refreshToken,
        );
        return res.data;
      },

      // Get product analytics
      getProductAnalytics: async (
        productId,
        startDate = null,
        endDate = null,
      ) => {
        let url = `products/${productId}/analytics`;
        if (startDate && endDate) {
          const params = new URLSearchParams({
            start_date: startDate,
            end_date: endDate,
          });
          url += `?${params}`;
        }
        const res = await makeAuthenticatedRequest(url, refreshToken);
        return res.data;
      },

      // Get email campaign impact
      getEmailCampaignImpact: async (actionId, includeTransactions = false) => {
        const params = new URLSearchParams({
          include_transactions: includeTransactions,
        });
        const res = await makeAuthenticatedRequest(
          `email-campaigns/${actionId}/impact?${params}`,
          refreshToken,
        );
        return res.data;
      },

      // Get attribution overview
      getAttributionOverview: async (
        projectId,
        startDate = null,
        endDate = null,
        additionalParams = {},
      ) => {
        let url = `projects/${projectId}/attribution-overview`;
        const params = new URLSearchParams();

        if (startDate && endDate) {
          params.append('start_date', startDate);
          params.append('end_date', endDate);
        }

        // Add additional params (e.g., store_id)
        Object.keys(additionalParams).forEach((key) => {
          if (additionalParams[key]) {
            params.append(key, additionalParams[key]);
          }
        });

        if (params.toString()) {
          url += `?${params}`;
        }

        const res = await makeAuthenticatedRequest(url, refreshToken);
        return res.data;
      },

      // Get client attribution overview (all projects)
      getClientAttributionOverview: async (
        clientId,
        startDate = null,
        endDate = null,
        additionalParams = {},
      ) => {
        let url = `clients/${clientId}/attribution-overview`;
        const params = new URLSearchParams();

        if (startDate && endDate) {
          params.append('start_date', startDate);
          params.append('end_date', endDate);
        }

        // Add additional params (e.g., store_id)
        Object.keys(additionalParams).forEach((key) => {
          if (additionalParams[key]) {
            params.append(key, additionalParams[key]);
          }
        });

        if (params.toString()) {
          url += `?${params}`;
        }

        const res = await makeAuthenticatedRequest(url, refreshToken);
        return res.data;
      },

      // List products
      getProducts: async (clientId, filters = {}) => {
        const params = new URLSearchParams(filters);
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/products?${params}`,
          refreshToken,
        );
        return res.data;
      },

      // Get single product
      getProduct: async (clientId, productId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/products/${productId}`,
          refreshToken,
        );
        return res.data;
      },

      // Create product
      createProduct: async (clientId, productData, imageFile = null) => {
        if (imageFile) {
          const formData = buildProductFormData(productData, imageFile);
          const res = await makeAuthenticatedRequest(
            `clients/${clientId}/products`,
            refreshToken,
            {
              method: 'post',
              data: formData,
              headers: { 'Content-Type': 'multipart/form-data' },
            },
          );
          return res.data;
        }
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/products`,
          refreshToken,
          {
            method: 'post',
            data: productData,
          },
        );
        return res.data;
      },

      // Update product
      updateProduct: async (productId, productData, imageFile = null) => {
        if (imageFile) {
          const formData = buildProductFormData(productData, imageFile);
          const res = await makeAuthenticatedRequest(
            `products/${productId}`,
            refreshToken,
            {
              method: 'put',
              data: formData,
              headers: { 'Content-Type': 'multipart/form-data' },
            },
          );
          return res.data;
        }
        const res = await makeAuthenticatedRequest(
          `products/${productId}`,
          refreshToken,
          {
            method: 'put',
            data: productData,
          },
        );
        return res.data;
      },

      // Delete product (soft delete)
      deleteProduct: async (productId) => {
        const res = await makeAuthenticatedRequest(
          `products/${productId}`,
          refreshToken,
          {
            method: 'delete',
          },
        );
        return res.data;
      },

      // Bulk upload products
      bulkUploadProducts: async (clientId, projectId, products) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/products/bulk-upload`,
          refreshToken,
          {
            method: 'post',
            data: products,
          },
        );
        return res.data;
      },
      updateClient: async (clientId, data) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}`,
          refreshToken,
          { method: 'put', data },
        );
        return res.data;
      },
      uploadClientImage: async (clientId, form) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/images`,
          refreshToken,
          { method: 'put', data: form },
        );
        return res.data;
      },
      uploadProductImage: async (productId, form) => {
        const res = await makeAuthenticatedRequest(
          `products/${productId}/image`,
          refreshToken,
          { method: 'put', data: form },
        );
        return res.data;
      },

      // ========== ADMIN API KEYS MANAGEMENT (Superadmin) ==========

      /**
       * Get all API keys across all clients (superadmin only)
       * @param {number} clientId - Optional filter by client ID
       */
      getApiKeys: async (clientId = null) => {
        let url = 'admin/api-keys';
        if (clientId) url += `?client_id=${clientId}`;
        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return Array.isArray(res.data) ? res.data : [];
      },

      /**
       * Get a specific API key details (superadmin only)
       * @param {number} apiKeyId - The API key ID
       */
      getApiKey: async (apiKeyId) => {
        const res = await makeAuthenticatedRequest(
          `admin/api-keys/${apiKeyId}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      /**
       * Create a new API key for a client (superadmin only)
       * @param {number} clientId - The client ID
       * @param {string[]} allowedIps - Optional initial allowed IPs
       */
      createApiKey: async (clientId, allowedIps = []) => {
        const res = await makeAuthenticatedRequest(
          'admin/api-keys',
          refreshToken,
          {
            method: 'post',
            data: {
              client_id: clientId,
              allowed_ips: allowedIps,
            },
          },
        );
        return res.data;
      },

      /**
       * Delete/revoke an API key (superadmin only)
       * @param {number} apiKeyId - The API key ID
       */
      deleteApiKey: async (apiKeyId) => {
        const res = await makeAuthenticatedRequest(
          `admin/api-keys/${apiKeyId}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      /**
       * Get allowed IPs for an API key (superadmin only)
       * @param {number} apiKeyId - The API key ID
       */
      getApiKeyAllowedIps: async (apiKeyId) => {
        const res = await makeAuthenticatedRequest(
          `admin/api-keys/${apiKeyId}/allowed-ips`,
          refreshToken,
          { method: 'get' },
        );
        return Array.isArray(res.data) ? res.data : [];
      },

      /**
       * Add an allowed IP to an API key (superadmin only)
       * @param {number} apiKeyId - The API key ID
       * @param {string} ipCidr - The IP or CIDR to add
       */
      addApiKeyAllowedIp: async (apiKeyId, ipCidr) => {
        const res = await makeAuthenticatedRequest(
          `admin/api-keys/${apiKeyId}/allowed-ips`,
          refreshToken,
          {
            method: 'post',
            data: { ip_cidr: ipCidr },
          },
        );
        return res.data;
      },

      /**
       * Remove an allowed IP from an API key (superadmin only)
       * @param {number} apiKeyId - The API key ID
       * @param {number} ipId - The allowed IP record ID
       */
      removeApiKeyAllowedIp: async (apiKeyId, ipId) => {
        const res = await makeAuthenticatedRequest(
          `admin/api-keys/${apiKeyId}/allowed-ips/${ipId}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      /**
       * Bulk add allowed IPs to an API key (superadmin only)
       * @param {number} apiKeyId - The API key ID
       * @param {string[]} ipCidrs - Array of IPs/CIDRs to add
       */
      bulkAddApiKeyAllowedIps: async (apiKeyId, ipCidrs) => {
        const res = await makeAuthenticatedRequest(
          `admin/api-keys/${apiKeyId}/allowed-ips/bulk`,
          refreshToken,
          {
            method: 'post',
            data: { ip_cidrs: ipCidrs },
          },
        );
        return res.data;
      },

      // ========== AI CAMPAIGN PROPOSALS ==========

      /**
       * Get all AI campaign proposals for the current user's client
       * @param {string} status - Optional filter by status
       * @param {number} page - Page number (default 1)
       * @param {number} perPage - Items per page (default 20)
       */
      getAIProposals: async (status = null, page = 1, perPage = 20) => {
        let url = `ai/campaign-proposals?page=${page}&per_page=${perPage}`;
        if (status) url += `&status=${status}`;
        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return res.data;
      },

      /**
       * Get a specific AI campaign proposal with full details
       * @param {number} proposalId - The proposal ID
       * @param {boolean} includeAgentOutputs - Include raw agent outputs
       */
      getAIProposal: async (proposalId, includeAgentOutputs = false) => {
        const url = `ai/campaign-proposals/${proposalId}?include_agent_outputs=${includeAgentOutputs}`;
        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return res.data;
      },

      /**
       * Review (approve/reject) an AI campaign proposal
       * @param {number} proposalId - The proposal ID
       * @param {object} reviewData - Review data
       * @param {string} reviewData.decision - 'approved', 'rejected', or 'approved_with_edits'
       * @param {string} reviewData.rejection_reason - Reason if rejected
       * @param {array} reviewData.edits_made - Edits if approved_with_edits
       * @param {number} reviewData.rating - 1-5 rating
       * @param {string} reviewData.comments - Comments
       * @param {boolean} reviewData.implement_now - If true, also create the campaign
       */
      reviewAIProposal: async (proposalId, reviewData) => {
        const res = await makeAuthenticatedRequest(
          `ai/campaign-proposals/${proposalId}/review`,
          refreshToken,
          {
            method: 'post',
            data: reviewData,
          },
        );
        return res.data;
      },

      /**
       * Implement an approved AI campaign proposal (create Project + EmailTemplates)
       * @param {number} proposalId - The proposal ID
       */
      implementAIProposal: async (proposalId) => {
        const res = await makeAuthenticatedRequest(
          `ai/campaign-proposals/${proposalId}/implement`,
          refreshToken,
          {
            method: 'post',
          },
        );
        return res.data;
      },

      /**
       * Get AI proposal statistics for the current client
       */
      getAIProposalStats: async () => {
        const res = await makeAuthenticatedRequest(
          'ai/campaign-proposals/stats',
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      /**
       * Persist an inline edit to a single email template on a pending proposal.
       * Backs the EmailBuilder integration on the proposal review page.
       * @param {number} proposalId - The proposal ID
       * @param {object} payload - { index, subject?, preview_text?, body_html?, body_text?, name? }
       */
      saveProposalEmailDraft: async (proposalId, payload) => {
        const res = await makeAuthenticatedRequest(
          `ai/campaign-proposals/${proposalId}/draft`,
          refreshToken,
          { method: 'patch', data: payload },
        );
        return res.data;
      },

      // ========== AI SINGLE TEMPLATE PROPOSALS ==========

      /**
       * Get all AI single template proposals for the current user's client
       * @param {string} status - Optional filter by status
       * @param {number} page - Page number (default 1)
       * @param {number} perPage - Items per page (default 20)
       */
      getSingleTemplateProposals: async (status = null, page = 1, perPage = 20) => {
        let url = `ai/campaign-proposals?proposal_type=single_template&page=${page}&per_page=${perPage}`;
        if (status) url += `&status=${status}`;
        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return res.data;
      },

      /**
       * Get single template proposals for a specific project
       * @param {number} projectId - The project ID
       * @param {string} status - Optional filter by status
       */
      getProjectSingleTemplateProposals: async (projectId, status = null, page = 1, perPage = 20) => {
        let url = `ai/projects/${projectId}/single-template-proposals?page=${page}&per_page=${perPage}`;
        if (status) url += `&status=${status}`;
        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return res.data;
      },

      /**
       * Get a specific single template proposal with full details
       * @param {number} proposalId - The proposal ID
       * @param {boolean} includeAgentOutputs - Include raw agent outputs
       */
      getSingleTemplateProposal: async (proposalId, includeAgentOutputs = false) => {
        const url = `ai/single-template-proposals/${proposalId}?include_agent_outputs=${includeAgentOutputs}`;
        const res = await makeAuthenticatedRequest(url, refreshToken, {
          method: 'get',
        });
        return res.data;
      },

      /**
       * Review (approve/reject) a single template proposal
       * @param {number} proposalId - The proposal ID
       * @param {object} reviewData - Review data
       * @param {string} reviewData.decision - 'approved', 'rejected', or 'approved_with_edits'
       * @param {string} reviewData.rejection_reason - Reason if rejected
       * @param {number} reviewData.rating - 1-5 rating
       * @param {string} reviewData.comments - Comments
       * @param {boolean} reviewData.apply_now - If true, also apply the template immediately
       * @param {boolean} reviewData.overwrite_existing - If true, overwrite existing template
       */
      reviewSingleTemplateProposal: async (proposalId, reviewData) => {
        const res = await makeAuthenticatedRequest(
          `ai/single-template-proposals/${proposalId}/review`,
          refreshToken,
          {
            method: 'post',
            data: reviewData,
          },
        );
        return res.data;
      },

      /**
       * Apply an approved single template proposal (create/update EmailTemplate)
       * @param {number} proposalId - The proposal ID
       * @param {boolean} overwriteExisting - Overwrite if template exists (default true)
       */
      applySingleTemplateProposal: async (proposalId, overwriteExisting = true) => {
        const res = await makeAuthenticatedRequest(
          `ai/single-template-proposals/${proposalId}/apply`,
          refreshToken,
          {
            method: 'post',
            data: { overwrite_existing: overwriteExisting },
          },
        );
        return res.data;
      },

      // --- CLIENT IMAGES METHODS ---
      /**
       * Get all images for a client
       * @param {number} clientId - Client ID
       * @param {string} category - Filter by category (optional)
       * @param {string} tags - Comma-separated tags (optional)
       */
      getImageBankImages: async (clientId, category = null, tags = null) => {
        let url = `clients/${clientId}/image-bank`;
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (tags) params.append('tags', tags);
        if (params.toString()) url += `?${params}`;

        const res = await makeAuthenticatedRequest(url, refreshToken);
        return res.data;
      },

      /**
       * Upload a new image for a client
       * @param {number} clientId - Client ID
       * @param {File} file - Image file
       * @param {Object} metadata - Image metadata
       */
      uploadImageBankImage: async (clientId, file, metadata = {}) => {
        const formData = new FormData();
        formData.append('file', file);
        if (metadata.alt_text) formData.append('alt_text', metadata.alt_text);
        if (metadata.category) formData.append('category', metadata.category);
        if (metadata.tags && Array.isArray(metadata.tags)) {
          formData.append('tags', metadata.tags.join(','));
        }
        if (metadata.custom_data) {
          formData.append('custom_data', JSON.stringify(metadata.custom_data));
        }

        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/image-bank`,
          refreshToken,
          {
            method: 'post',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );
        return res.data;
      },

      /**
       * Update image metadata
       * @param {number} clientId - Client ID
       * @param {number} imageId - Image ID
       * @param {Object} metadata - Updated metadata
       */
      updateImageBankImage: async (clientId, imageId, metadata) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/image-bank/${imageId}`,
          refreshToken,
          {
            method: 'put',
            data: metadata,
          },
        );
        return res.data;
      },

      /**
       * Delete an image
       * @param {number} clientId - Client ID
       * @param {number} imageId - Image ID
       */
      deleteImageBankImage: async (clientId, imageId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/image-bank/${imageId}`,
          refreshToken,
          {
            method: 'delete',
          },
        );
        return res.data;
      },

      /**
       * Get template context for client images (for debugging)
       * @param {number} clientId - Client ID
       */
      getImageBankTemplateContext: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/image-bank/template-context`,
          refreshToken,
        );
        return res.data;
      },

      // ========== AI ONBOARDING ==========

      /**
       * Get brand identity data for a client
       * @param {number} clientId - The client ID
       */
      getClientBrandIdentity: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `ai/onboarding/brand-identity/${clientId}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      /**
       * Import an HTML template into the brand kit (client-level, no project).
       * @param {number} clientId
       * @param {File} file - .zip, .html or .htm
       */
      importBrandKitHtml: async (clientId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        const res = await makeAuthenticatedRequest(
          `ai/onboarding/brand-kit/${clientId}/import-html`,
          refreshToken,
          {
            method: 'post',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        );
        return res.data;
      },

      /**
       * Trigger AI onboarding context generation for a client
       * @param {object} data - { client_id, website?, industry?, company_size? }
       */
      triggerOnboardingContext: async (data) => {
        const res = await makeAuthenticatedRequest(
          'ai/onboarding/trigger',
          refreshToken,
          { method: 'post', data },
        );
        return res.data;
      },

      /**
       * Get the status of an AI onboarding job
       * @param {string} jobId - The job ID returned by trigger
       */
      getOnboardingStatus: async (jobId) => {
        const res = await makeAuthenticatedRequest(
          `ai/onboarding/status/${jobId}`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      // ─── Onboarding Checklist ───────────────────────────────

      /**
       * Get onboarding checklist status (auto-detected steps)
       * @param {number} clientId
       */
      getOnboardingChecklist: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/onboarding`,
          refreshToken,
          { method: 'get' },
        );
        return res.data;
      },

      /**
       * Manually mark an onboarding step as completed
       * @param {number} clientId
       * @param {string} stepKey
       */
      markOnboardingStep: async (clientId, stepKey) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/onboarding/step`,
          refreshToken,
          { method: 'put', data: { step_key: stepKey } },
        );
        return res.data;
      },

      /**
       * Dismiss the onboarding checklist
       * @param {number} clientId
       */
      dismissOnboarding: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/onboarding/dismiss`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      /**
       * Re-open the onboarding checklist after dismissal
       * @param {number} clientId
       */
      reopenOnboarding: async (clientId) => {
        const res = await makeAuthenticatedRequest(
          `clients/${clientId}/onboarding/reopen`,
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },

      // ===================================================================
      // MARKETING ANALYTICS
      // ===================================================================

      getMarketingOverview: async (startDate, endDate) => {
        const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/overview?${params}`,
          refreshToken,
        );
        return res.data;
      },

      getMarketingTimeline: async (startDate, endDate, source) => {
        const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
        if (source) params.append('source', source);
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/timeline?${params}`,
          refreshToken,
        );
        return res.data;
      },

      getMarketingComparison: async (startDate, endDate, source) => {
        const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
        if (source) params.append('source', source);
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/comparison?${params}`,
          refreshToken,
        );
        return res.data;
      },

      getMarketingMultiComparison: async (referenceDate, granularity = 'daily', source) => {
        const params = new URLSearchParams({ reference_date: referenceDate, granularity });
        if (source) params.append('source', source);
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/multi-comparison?${params}`,
          refreshToken,
        );
        return res.data;
      },

      getMarketingGranularTimeline: async (startDate, endDate, granularity = 'daily', source, metric = 'spend') => {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          granularity,
          metric,
        });
        if (source) params.append('source', source);
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/granular-timeline?${params}`,
          refreshToken,
        );
        return res.data;
      },

      getMarketingCampaigns: async (startDate, endDate, sortBy = 'spend') => {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          sort_by: sortBy,
        });
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/campaigns?${params}`,
          refreshToken,
        );
        return res.data;
      },

      getMarketingIntegrations: async () => {
        const res = await makeAuthenticatedRequest(
          'marketing-analytics/integrations',
          refreshToken,
        );
        return res.data;
      },

      connectMarketingProvider: async (provider) => {
        const res = await makeAuthenticatedRequest(
          'marketing-analytics/connect',
          refreshToken,
          { method: 'post', data: { provider } },
        );
        return res.data;
      },

      disconnectMarketingIntegration: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}`,
          refreshToken,
          { method: 'delete' },
        );
        return res.data;
      },

      finalizeMarketingAccount: async (pickState, provider, accountId, accountName) => {
        const res = await makeAuthenticatedRequest(
          'marketing-analytics/finalize-account',
          refreshToken,
          { method: 'post', data: { pick_state: pickState, provider, account_id: accountId, account_name: accountName } },
        );
        return res.data;
      },

      /**
       * Request incremental OAuth scope expansion for a marketing integration.
       * Currently only Meta Ads supports this; the backend returns an auth_url
       * that should be opened (typically via window.location.href) so the
       * user can re-consent with the additional bundle's permissions.
       *
       * @param {number} integrationId - MarketingIntegration row id
       * @param {string} bundle - Scope bundle key (e.g. "ads_optimize")
       * @returns {Promise<{auth_url: string}>}
       */
      expandMarketingScopes: async (integrationId, bundle) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/expand-scopes`,
          refreshToken,
          { method: 'post', data: { bundle } },
        );
        return res.data;
      },

      /**
       * List the Facebook Pages the connected Meta user manages (issue #1169).
       * Needs only `pages_show_list` (base bundle), so it works before the
       * `pages_read_engagement` grant.
       *
       * @param {number} integrationId - MarketingIntegration row id
       * @returns {Promise<{pages: Array<{id, name, fan_count, picture_url}>}>}
       */
      listMetaPages: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/pages`,
          refreshToken,
        );
        return res.data;
      },

      /**
       * Fetch one Page's organic engagement (insights + recent posts).
       * Requires `pages_read_engagement`; without it the backend returns
       * 403 `missing_scope` and the ScopeGate prompts for re-consent.
       *
       * @param {number} integrationId - MarketingIntegration row id
       * @param {string} pageId - Facebook Page id
       * @returns {Promise<{page_id, page_name, insights, recent_posts}>}
       */
      getMetaPageEngagement: async (integrationId, pageId) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/pages/${encodeURIComponent(pageId)}/engagement`,
          refreshToken,
        );
        return res.data;
      },

      /**
       * Patch a marketing integration's config (e.g. remember the selected Page).
       * The backend merges the `config` object into the existing config.
       *
       * @param {number} integrationId - MarketingIntegration row id
       * @param {object} payload - e.g. { config: { selected_page_id, selected_page_name } }
       * @returns {Promise<object>} updated integration
       */
      updateMarketingIntegration: async (integrationId, payload) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}`,
          refreshToken,
          { method: 'patch', data: payload },
        );
        return res.data;
      },

      /**
       * Pause a Meta Ads campaign. Sends `confirm: true` — the backend now
       * requires it (human-in-the-loop) plus the per-client `ads` feature flag.
       * If the integration lacks the `ads_management` scope the backend returns
       * 403 `missing_scope` and the global axios interceptor surfaces the
       * re-auth flow; without the feature it returns 403 `feature_disabled`.
       */
      pauseMetaCampaign: async (integrationId, campaignId) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/campaigns/${encodeURIComponent(campaignId)}/pause`,
          refreshToken,
          { method: 'post', data: { confirm: true } },
        );
        return res.data;
      },

      /**
       * Resume a paused Meta Ads campaign. See pauseMetaCampaign for the
       * scope/feature gating and the `confirm: true` requirement.
       */
      resumeMetaCampaign: async (integrationId, campaignId) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/campaigns/${encodeURIComponent(campaignId)}/resume`,
          refreshToken,
          { method: 'post', data: { confirm: true } },
        );
        return res.data;
      },

      /**
       * Fetch live campaign statuses from Meta for an integration's ad account.
       * Returns `{ statuses: { campaign_id: 'ACTIVE' | 'PAUSED' | ... } }`.
       * `campaignIds` is optional; when provided the request is narrowed to
       * just those campaigns.
       */
      getMetaCampaignStatuses: async (integrationId, campaignIds) => {
        const params = new URLSearchParams();
        if (campaignIds && campaignIds.length) {
          params.append('ids', campaignIds.join(','));
        }
        const qs = params.toString();
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/campaigns/statuses${qs ? `?${qs}` : ''}`,
          refreshToken,
        );
        return res.data;
      },

      /**
       * Fetch Isi's read-only optimization suggestions for a single campaign.
       * The endpoint always returns a valid envelope (may carry an empty
       * `suggestions` array plus `notes`), so callers can render an empty /
       * onboarding state without special-casing errors.
       *
       * @param {number} integrationId - MarketingIntegration row id
       * @param {string} campaignExternalId - provider campaign id
       * @param {object} [options]
       * @param {boolean} [options.force] - bypass the backend's 6h cache and
       *   regenerate the suggestions (sends `?force=true`).
       * @returns {Promise<object>} suggestion envelope (see Ads spec output schema)
       */
      getCampaignSuggestions: async (integrationId, campaignExternalId, { force = false } = {}) => {
        const qs = force ? '?force=true' : '';
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/campaigns/${encodeURIComponent(campaignExternalId)}/suggestions${qs}`,
          refreshToken,
        );
        return res.data;
      },

      /**
       * Fetch per-campaign metrics for a date range (Increment F2). Returns
       * `{ currency, range, totals, previous, deltas, daily, recent_activity,
       * last_active_date }`. Used by the campaign detail page's Resumen /
       * Rendimiento tabs (KPI scorecards, trend chart, funnel, daily table).
       *
       * @param {number} integrationId - MarketingIntegration row id
       * @param {string} campaignExternalId - provider campaign id
       * @param {object} options
       * @param {string} options.startDate - ISO date (YYYY-MM-DD)
       * @param {string} options.endDate - ISO date (YYYY-MM-DD)
       */
      getCampaignMetrics: async (integrationId, campaignExternalId, { startDate, endDate } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        const qs = params.toString();
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/campaigns/${encodeURIComponent(campaignExternalId)}/metrics${qs ? `?${qs}` : ''}`,
          refreshToken,
        );
        return res.data;
      },

      /**
       * Apply a budget suggestion to a Google Ads campaign (Increment B write).
       * `body` is the suggestion's `apply` object passed verbatim plus
       * `confirm: true` and `suggestion_id`. The backend re-validates the
       * stale-write guard and bounds server-side; the FE never converts values.
       *
       * Error contract (handled by the caller): 409 stale-write,
       * 403 `missing_scope` (global interceptor), 403 `google_ads_authorization`,
       * 400 validation.
       */
      applyGoogleAdsBudget: async (integrationId, campaignExternalId, body) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/google-ads/campaigns/${encodeURIComponent(campaignExternalId)}/budget`,
          refreshToken,
          { method: 'post', data: body },
        );
        return res.data;
      },

      /**
       * Apply a bid-strategy suggestion to a Google Ads campaign (Increment B).
       * See applyGoogleAdsBudget for the body convention and error contract.
       */
      applyGoogleAdsBidStrategy: async (integrationId, campaignExternalId, body) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/google-ads/campaigns/${encodeURIComponent(campaignExternalId)}/bid-strategy`,
          refreshToken,
          { method: 'post', data: body },
        );
        return res.data;
      },

      /**
       * Create a new responsive search ad in a Google Ads campaign (Increment
       * F4). RSA copy is immutable, so "editing" copy = create-new + optionally
       * pause the old ad. `body` = `{ ad_group_id, headlines: [str],
       * descriptions: [str], final_urls: [str], path1?, path2?,
       * pause_ad_resource_name?, confirm: true, suggestion_id?, validate_only? }`.
       * The backend re-validates RSA limits server-side (3-15 headlines ≤30,
       * 2-4 descriptions ≤90, ≥1 http(s) URL).
       *
       * Error contract (handled by the caller): 403 `missing_scope` (global
       * interceptor → re-auth), 403 `google_ads_authorization` (write access /
       * developer token), 401 `google_ads_authentication`, 400
       * `google_ads_api_error` (RSA validation rejected by Google or by us).
       */
      createGoogleAdsRsa: async (integrationId, campaignExternalId, body) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/google-ads/campaigns/${encodeURIComponent(campaignExternalId)}/ads`,
          refreshToken,
          { method: 'post', data: body },
        );
        return res.data;
      },

      /**
       * Pause a Google Ads campaign (status → PAUSED). Parity with Meta's
       * pause control. `body` = `{ confirm: true, suggestion_id?,
       * validate_only? }` — `confirm: true` is required by the backend.
       * On success returns `{ campaign_id, field: 'status', old_value,
       * new_value, noop, request_id?, updated_at? }`; `noop: true` means the
       * campaign was already paused.
       *
       * Error contract (handled by the caller): 403 `missing_scope` (global
       * interceptor → re-auth), 403 `google_ads_authorization` (write access /
       * role in the account), 401 `google_ads_authentication`, 400
       * `google_ads_api_error` (the `error` carries the reason, e.g. campaign
       * REMOVED), 403 `feature_disabled`.
       */
      pauseGoogleAdsCampaign: async (integrationId, campaignExternalId, body) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/google-ads/campaigns/${encodeURIComponent(campaignExternalId)}/pause`,
          refreshToken,
          { method: 'post', data: body },
        );
        return res.data;
      },

      /**
       * Resume a Google Ads campaign (status → ENABLED). See
       * pauseGoogleAdsCampaign for the body convention and error contract.
       * `noop: true` means the campaign was already active.
       */
      resumeGoogleAdsCampaign: async (integrationId, campaignExternalId, body) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/google-ads/campaigns/${encodeURIComponent(campaignExternalId)}/resume`,
          refreshToken,
          { method: 'post', data: body },
        );
        return res.data;
      },

      /**
       * Fetch the applied budget/bid change history for a Google Ads
       * integration (Increment C). Returns `{ changes: [...], total }` with each
       * row already formatted server-side (`*_display`). Google Ads only — Meta
       * has no change history.
       */
      getCampaignChanges: async (integrationId) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/changes`,
          refreshToken,
        );
        return res.data;
      },

      /**
       * Revert a previously-applied Google Ads change (Increment C). Replays the
       * inverse mutation server-side with a stale-write guard.
       *
       * Error contract (handled by the caller): 409 `stale_write` (live value
       * moved since the change), other → generic error.
       */
      revertCampaignChange: async (integrationId, changeId) => {
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/integrations/${integrationId}/changes/${changeId}/revert`,
          refreshToken,
          { method: 'post', data: { confirm: true } },
        );
        return res.data;
      },

      triggerMarketingSync: async (integrationId, dateFrom, dateTo) => {
        const data = {};
        if (dateFrom) data.date_from = dateFrom;
        if (dateTo) data.date_to = dateTo;
        const res = await makeAuthenticatedRequest(
          `marketing-analytics/sync/${integrationId}`,
          refreshToken,
          { method: 'post', data },
        );
        return res.data;
      },

      /**
       * Handoff de sesión a isi-next (#1006): pide al backend un token de UN SOLO
       * USO (TTL 45s) para abrir next.getisi.ai ya con sesión, sin exponer el
       * refresh token (30d) en la URL como hace el handoff del Planner.
       * Devuelve { handoff_token, expires_in }.
       */
      createNextHandoff: async () => {
        const res = await makeAuthenticatedRequest(
          'auth/handoff',
          refreshToken,
          { method: 'post' },
        );
        return res.data;
      },
    }),
    [refreshToken],
  );
}
